import { io } from "socket.io-client";
import { createContext, ReactNode, useEffect, useState } from "react";
import axios from "axios";

// Define a type for your socket context
type SocketContextType = ReturnType<typeof io> | null;
const SocketContext = createContext<SocketContextType>(null);

interface SocketProviderProps {
  children: ReactNode;
}

const checkServerHealth = async () => {
  try {
    const serverHealth = await axios.get(
      `${import.meta.env.VITE_WEBSOCKET_SERVER_DOMAIN}/healthz`
    );
    return serverHealth.status === 200;
  } catch {
    return false;
  }
};

const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [serverIsHealthy, setServerIsHealthy] = useState<boolean | null>(null);
  const [socket, setSocket] = useState<SocketContextType>(null);

  useEffect(() => {
    let socketInstance: SocketContextType = null;
    let retryTimeout: ReturnType<typeof setTimeout>;

    const verifyServer = async () => {
      if (import.meta.env.VITE_ENVIRONMENT === "production") {
        const isHealthy = await checkServerHealth();
        setServerIsHealthy(isHealthy);

        if (isHealthy) {
          // Server is healthy; initialize the socket connection
          socketInstance = io(import.meta.env.VITE_WEBSOCKET_SERVER_DOMAIN, {
            transports: ["websocket"],
          });
          setSocket(socketInstance);
        } else {
          // Server is down; schedule a retry after 50 seconds
          retryTimeout = setTimeout(verifyServer, 50000);
        }
      } else {
        // In non-production, assume the server is healthy
        setServerIsHealthy(true);
        socketInstance = io(import.meta.env.VITE_WEBSOCKET_SERVER_DOMAIN, {
          transports: ["websocket"],
        });
        setSocket(socketInstance);
      }
    };

    verifyServer();

    // Cleanup function: disconnect the socket and clear any pending timeouts
    return () => {
      if (socketInstance) {
        socketInstance.disconnect();
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, []);

  if (serverIsHealthy === null)
    return <div>Checking server status...</div>;
  if (!serverIsHealthy)
    return <div>Server is not running. Retrying in 50 seconds...</div>;

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};

export { SocketProvider, SocketContext };
